import { startCase } from 'lodash';

import Workflow, { WorkflowAction, WorkflowFacetOptions, WorkflowValue } from 'models/Workflow';

import { VALUE_NAMES } from './constants';
import { COVERAGE_SEQUENCES, COVERAGES_VALUE, coverageSequence, ExtractedCoverage } from './coverages';
import { ssnDigits } from './ssn';

// Where the packet disagrees with the value the referral is using (OLIO-3252).
//
// A typed or portal value outranks extraction (manual > integration > action), so when a packet
// later says something different the packet's reading simply loses — and until this, nothing on the
// page said so. Extraction's own reading is still on the completed Patient Demographics step's
// outputs; this compares it, field by field, with the value in use, and the card highlights what
// differs.
//
// Referral source is deliberately not compared. Confirming a source writes the client's reporting
// name over the packet's wording on purpose (the API's ResolveAttrValues), so the two differ on every
// confirmed referral, and "Use this value" would put the raw wording back and stop it reporting.

// Stored on the step's outputs by Dismiss; the API validates, stamps and carries these (the Rails
// ExtractAttributes::Dismissals). An entry names a field, never its value.
export const DISMISSALS_OUTPUT = 'demographic_conflict_dismissals';

// The scalar fields compared, in card order. Names are one card cell ("Name") over two values.
const NAME_VALUES = [VALUE_NAMES.PATIENT_FIRST_NAME, VALUE_NAMES.PATIENT_LAST_NAME];
const SCALAR_VALUES = [VALUE_NAMES.PATIENT_DOB, VALUE_NAMES.PATIENT_GENDER, VALUE_NAMES.PATIENT_SSN];
export const ADDRESS_VALUES = [
  VALUE_NAMES.PATIENT_ADDRESS_LINE_1,
  VALUE_NAMES.PATIENT_ADDRESS_LINE_2,
  VALUE_NAMES.PATIENT_CITY,
  VALUE_NAMES.PATIENT_STATE,
  VALUE_NAMES.PATIENT_ZIP,
];
const COVERAGE_FIELDS = [VALUE_NAMES.PAYER_NAME, VALUE_NAMES.MEMBER_ID] as const;

// A field as a dismissal names it: the value name, plus which coverage for a coverage field.
// `inUseAt` stamps the value in use the comparison was made against — when it was last written — so a
// dismissal stops holding once someone edits that value (see `dismissed` below). null where the
// referral holds no value there (a coverage it doesn't have).
export interface ConflictField {
  name: string;
  sequence?: string;
  inUseAt: string | null;
}

// One highlighted card cell.
export interface DemographicConflict {
  // Matches the card cell it belongs to — see ExtractionResults' cell keys.
  key: string;
  // Every differing field under the cell (a name conflict can be first, last, or both).
  fields: ConflictField[];
  // What "Use this value" writes, as { value name: packet value }.
  values: Record<string, string>;
  // Scalar or same-rank coverage: the live coverage these fields belong to. Undefined for a
  // referral-level field.
  facetId?: string;
  // A coverage the packet lists at a rank the referral holds nothing at — "Use" adds it.
  missingCoverage?: { position: number; label: string };
  dismissed: boolean;
}

// A conflict before its dismissal state is read.
type Found = Omit<DemographicConflict, 'dismissed'>;

export interface DismissalEntry {
  name: string;
  sequence?: string;
  inUseAt?: string | null;
}

// The recorded dismissals, narrowed defensively the way HighCostMedsAction reads its reviews —
// keys arrive camelCased, the value NAMES inside them do not.
export function recordedDismissals(action: WorkflowAction): DismissalEntry[] {
  const raw = action.outputFor(DISMISSALS_OUTPUT);
  if (!Array.isArray(raw)) return [];

  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const { name, sequence, inUseAt } = entry as Record<string, unknown>;
    if (typeof name !== 'string') return [];
    return [
      {
        name,
        ...(typeof sequence === 'string' ? { sequence } : {}),
        inUseAt: typeof inUseAt === 'string' ? inUseAt : null,
      },
    ];
  });
}

const sameField = (a: Omit<ConflictField, 'inUseAt'>, b: Omit<ConflictField, 'inUseAt'>) =>
  a.name === b.name && (a.sequence ?? '') === (b.sequence ?? '');

// A dismissal holds while the value in use is the one it was made against. Rows are append-only, so a
// new write is a new row with its own updated_at.
const stamp = (value: WorkflowValue | undefined): string | null => value?.updatedAt?.toISOString() ?? null;

// ---- normalizing ----------------------------------------------------------------------------

const text = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

// "Robert A." and "robert a", "O'Brien" and "OBrien", "Smith-Jones" and "Smith Jones" are one name
// written two ways, not a disagreement.
const personName = (value: string) => text(value.replace(/[.,'’]/g, '').replace(/-/g, ' '));

// Member IDs are printed with and without separators; only the letters and digits identify one.
const memberId = (value: string) => value.replace(/[^a-z0-9]/gi, '').toLowerCase();

// Typed and portal DOBs are stored ISO; extraction's are asked for as a date. Compare the calendar
// day, so a timestamp or a different spelling of the same date is not a disagreement.
function day(value: string): string {
  const iso = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return iso.slice(1).join('-');
  // A non-ISO spelling parses as LOCAL midnight, so read it back in local terms — converting to UTC
  // would move it a day for anyone east of Greenwich.
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return text(value);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

function differs(name: string, current: string, packet: string): boolean {
  if (name === VALUE_NAMES.PATIENT_DOB) return day(current) !== day(packet);
  if (name === VALUE_NAMES.PATIENT_SSN) {
    const typed = ssnDigits(current);
    const read = ssnDigits(packet);
    // Extraction may read only the last four; a partial reading that agrees is not a conflict.
    return read.length < typed.length ? !typed.endsWith(read) : typed !== read;
  }
  if ((NAME_VALUES as readonly string[]).includes(name)) return personName(current) !== personName(packet);
  if (name === VALUE_NAMES.MEMBER_ID) return memberId(current) !== memberId(packet);
  return text(current) !== text(packet);
}

const packetString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

// A value only conflicts when a person or a portal set it: an extraction-sourced value IS the
// packet's reading (from this packet or an earlier one), which a re-extraction simply replaces.
const outranksPacket = (value: WorkflowValue | undefined): value is WorkflowValue =>
  !!value && value.source !== 'action' && typeof value.value === 'string' && value.value.trim() !== '';

// ---- comparing ------------------------------------------------------------------------------

function scalarConflict(workflow: Workflow, action: WorkflowAction, name: string): ConflictField | undefined {
  const packet = packetString(action.outputFor(name));
  const current = workflow.valueFor(name);
  if (!packet || !outranksPacket(current)) return undefined;
  return differs(name, current.value as string, packet) ? { name, inUseAt: stamp(current) } : undefined;
}

// Coverage-scoped reads are exact: the referral-level fallback in valueFor is for referrals that
// pre-date coverages, and comparing against it would pin one coverage's payer on another.
const facetValue = (workflow: Workflow, name: string, facetId: string) =>
  workflow.values.find((value) => value.name === name && value.facetId === facetId);

function packetCoverages(action: WorkflowAction): ExtractedCoverage[] {
  const raw = action.outputFor(COVERAGES_VALUE);
  if (!Array.isArray(raw)) return [];
  return raw.filter((entry): entry is ExtractedCoverage => !!entry && typeof entry === 'object');
}

// The coverage holding a rank: by its own sequence word, else — for one labelled in some other way —
// by the position that rank stands for. Never a coverage whose label claims a DIFFERENT rank.
function atRank(facets: WorkflowFacetOptions[], sequence: string) {
  const known: readonly string[] = COVERAGE_SEQUENCES;
  const position = known.indexOf(sequence);
  return (
    facets.find((facet) => coverageSequence(facet).toLowerCase() === sequence) ??
    facets.find((facet) => facet.position === position && !known.includes(coverageSequence(facet).toLowerCase()))
  );
}

// Whether the value in use reads the same as this step's reading of the packet, under the comparison
// conflicts use. Whoever wrote it: a typed or portal value the packet confirms, and a packet value a
// coordinator chose with "Use this value" (written as theirs), both read the packet's way.
export function isPacketReading(name: string, current: unknown, packet: unknown): boolean {
  const read = packetString(packet);
  if (!read || current == null || String(current).trim() === '') return false;
  return !differs(name, String(current), read);
}

// The packet's reading of the coverage a live coverage stands for, matched by rank the way conflicts
// match them, so a coverage labelled some other way still finds the packet's primary or secondary.
// The snapshot shown before coverages were minted is the packet's own list, matched by its word.
export function packetCoverageFor(
  workflow: Workflow,
  action: WorkflowAction,
  coverage: ExtractedCoverage
): ExtractedCoverage | undefined {
  const read = packetCoverages(action);
  const word = (entry: ExtractedCoverage) => entry.sequence?.toLowerCase();
  if (!coverage.facetId) return read.find((entry) => !!word(entry) && word(entry) === word(coverage));
  return read.find((entry) => {
    const sequence = word(entry);
    return !!sequence && atRank(workflow.coverageFacets, sequence)?.id === coverage.facetId;
  });
}

export function coverageConflictKey(field: string, sequence: string) {
  return `${field === VALUE_NAMES.PAYER_NAME ? 'payer' : 'member'}:${sequence}`;
}

export const PATIENT_NAME_KEY = 'patient-name';

export const ADDRESS_KEY = 'patient_address';

export const missingCoverageKey = (sequence: string) => `coverage:${sequence}`;

function coverageConflicts(workflow: Workflow, action: WorkflowAction): Found[] {
  // A referral that never had coverages minted shows the packet's own list (currentCoverages), so
  // there is nothing for the packet to disagree with — and nowhere to add one.
  if (!workflow.facets.some((facet) => facet.dimension === 'coverage')) return [];
  const live = workflow.coverageFacets;
  const archived = workflow.facets.filter((facet) => facet.dimension === 'coverage' && facet.archived);

  return packetCoverages(action).flatMap<Found>((coverage) => {
    const sequence = coverage.sequence?.toLowerCase();
    if (!sequence) return [];
    const read = {
      [VALUE_NAMES.PAYER_NAME]: packetString(coverage.payerName),
      [VALUE_NAMES.MEMBER_ID]: packetString(coverage.memberId),
    };

    const facet = atRank(live, sequence);
    if (!facet) {
      // A person archived the coverage at this rank — a deliberate removal the engine won't undo
      // either, so the packet listing it again is not news.
      if (atRank(archived, sequence)) return [];
      const position = COVERAGE_SEQUENCES.indexOf(sequence as (typeof COVERAGE_SEQUENCES)[number]);
      const values = Object.fromEntries(Object.entries(read).filter((pair): pair is [string, string] => !!pair[1]));
      if (position < 0 || Object.keys(values).length === 0) return [];

      return [
        {
          key: missingCoverageKey(sequence),
          fields: Object.keys(values).map((name) => ({ name, sequence, inUseAt: null })),
          values,
          // The same label the Edit Demographics form files a new coverage under — it is the claim on
          // the rank that stops a later extraction minting a second coverage there.
          missingCoverage: { position, label: startCase(sequence) },
        },
      ];
    }

    return COVERAGE_FIELDS.flatMap<Found>((name) => {
      const packet = read[name];
      const current = facetValue(workflow, name, facet.id);
      if (!packet || !outranksPacket(current) || !differs(name, current.value as string, packet)) return [];
      return [
        {
          key: coverageConflictKey(name, sequence),
          fields: [{ name, sequence, inUseAt: stamp(current) }],
          values: { [name]: packet },
          facetId: facet.id,
        },
      ];
    });
  });
}

// Raised by any differing part, but "Use this value" writes the packet's whole address: a packet street
// beside a typed city is an address nobody wrote. A part the packet didn't read keeps what's stored,
// except the suite, which a packet street without one clears — the rule extraction itself follows
// (the API's ExtractAttributes::Address). The API keeps a suite read beside a different typed street
// off the suite output, so it isn't stored next to that street, and records it as PACKET_SUITE.
const PACKET_SUITE = 'packet_address_line_2';

function addressConflict(workflow: Workflow, action: WorkflowAction): Found[] {
  const fields = ADDRESS_VALUES.flatMap((name) => scalarConflict(workflow, action, name) ?? []);
  if (fields.length === 0) return [];

  const values = Object.fromEntries(
    ADDRESS_VALUES.flatMap((name) => {
      const packet = packetString(action.outputFor(name));
      return packet ? [[name, packet]] : [];
    })
  );
  const suite = VALUE_NAMES.PATIENT_ADDRESS_LINE_2;
  const packetSuite = packetString(action.outputFor(PACKET_SUITE));
  if (packetSuite && !values[suite]) values[suite] = packetSuite;
  if (values[VALUE_NAMES.PATIENT_ADDRESS_LINE_1] && !values[suite] && workflow.valueFor(suite)?.value) {
    values[suite] = '';
  }
  return [{ key: ADDRESS_KEY, fields, values }];
}

export function demographicConflicts(workflow: Workflow, action: WorkflowAction): DemographicConflict[] {
  const nameFields = NAME_VALUES.flatMap((name) => scalarConflict(workflow, action, name) ?? []);
  const scalars: Found[] = [
    ...(nameFields.length > 0
      ? [{ key: PATIENT_NAME_KEY, fields: nameFields, values: packetValues(action, nameFields) }]
      : []),
    ...SCALAR_VALUES.flatMap((name) => {
      const field = scalarConflict(workflow, action, name);
      return field ? [{ key: name, fields: [field], values: packetValues(action, [field]) }] : [];
    }),
    ...addressConflict(workflow, action),
  ];

  const dismissals = recordedDismissals(action);
  return [...scalars, ...coverageConflicts(workflow, action)].map((conflict) => ({
    ...conflict,
    dismissed: conflict.fields.every((field) =>
      dismissals.some((entry) => sameField(entry, field) && (entry.inUseAt ?? null) === field.inUseAt)
    ),
  }));
}

function packetValues(action: WorkflowAction, fields: ConflictField[]): Record<string, string> {
  return Object.fromEntries(fields.map(({ name }) => [name, packetString(action.outputFor(name)) as string]));
}

// What one Dismiss sends: this conflict's fields and nothing else. The API ADDS them to the recorded
// dismissals rather than replacing the list, so two people dismissing at once can't erase each other.
export function dismissalsFor(conflict: DemographicConflict): DismissalEntry[] {
  return conflict.fields.map(({ name, sequence, inUseAt }) => ({ name, ...(sequence ? { sequence } : {}), inUseAt }));
}
